import { useState, useRef, useEffect, useLayoutEffect } from 'react';

export const useBottleCarousel = (totalItems) => {
	const [activeBottleIndex, setActiveBottleIndex] = useState(0);
	const [carouselOffset, setCarouselOffset] = useState(0);
	const [mobileBottleOffset, setMobileBottleOffset] = useState(0);
	const [touchStart, setTouchStart] = useState(null);
	const [touchEnd, setTouchEnd] = useState(null);

	const bottleSlideRefs = useRef([]);
	const detailSlideRefs = useRef([]);

	// Handle bottle click
	const handleBottleClick = (product, index) => {
		setActiveBottleIndex(index);

		if (bottleSlideRefs.current[index]) {
			const containerWidth = bottleSlideRefs.current[index].offsetWidth;
			setMobileBottleOffset(-index * containerWidth);
		}
	};

	// Handle carousel dot click
	const handleCarouselDotClick = (index) => {
		setActiveBottleIndex(index);
	};

	// Navigation
	const handleNext = () => {
		if (activeBottleIndex < totalItems - 1) {
			setActiveBottleIndex((i) => i + 1);
		}
	};

	const handlePrev = () => {
		if (activeBottleIndex > 0) {
			setActiveBottleIndex((i) => i - 1);
		}
	};

	// Touch handlers
	const handleTouchStart = (e) => {
		setTouchStart(e.targetTouches[0].clientX);
		setTouchEnd(null);
	};

	const handleTouchMove = (e) => {
		setTouchEnd(e.targetTouches[0].clientX);
	};

	const handleTouchEnd = () => {
		if (!touchStart || !touchEnd) return;
		const distance = touchStart - touchEnd;
		if (distance > 50) handleNext();
		if (distance < -50) handlePrev();
	};

	// Update offset when index changes
	useLayoutEffect(() => {
		const isMobileOrTablet = window.matchMedia('(max-width: 1024px)').matches;
		if (!isMobileOrTablet) return;

		const bottleSlide = bottleSlideRefs.current[activeBottleIndex];
		const detailSlide = detailSlideRefs.current[activeBottleIndex];

		const bottleWidth = bottleSlide?.offsetWidth || 0;
		const detailWidth = detailSlide?.offsetWidth || 0;

		setMobileBottleOffset(-activeBottleIndex * bottleWidth);
		setCarouselOffset(-activeBottleIndex * detailWidth);
	}, [activeBottleIndex]);

	// Recalculate on resize
	useEffect(() => {
		const handleResize = () => {
			const isMobileOrTablet = window.matchMedia('(max-width: 1024px)').matches;
			const bottleSlide = bottleSlideRefs.current[activeBottleIndex];
			const detailSlide = detailSlideRefs.current[activeBottleIndex];

			if (isMobileOrTablet && bottleSlide && detailSlide) {
				const bottleWidth = bottleSlide.offsetWidth;
				const detailWidth = detailSlide.offsetWidth;
				setMobileBottleOffset(-activeBottleIndex * bottleWidth);
				setCarouselOffset(-activeBottleIndex * detailWidth);
			} else {
				setMobileBottleOffset(0);
				setCarouselOffset(0);
			}
		};

		handleResize();
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [activeBottleIndex]);

	return {
		activeBottleIndex,
		setActiveBottleIndex,
		carouselOffset,
		mobileBottleOffset,
		bottleSlideRefs,
		detailSlideRefs,
		handleNext,
		handlePrev,
		handleBottleClick,
		handleCarouselDotClick,
		touchHandlers: {
			onTouchStart: handleTouchStart,
			onTouchMove: handleTouchMove,
			onTouchEnd: handleTouchEnd,
		},
	};
};
